import React, { useState, useEffect, useCallback } from 'react';
import { Toast, ToastAction } from './Toast.js';
import { ToastContext, ToastOptions, ToastContextValue } from './ToastContext.js';
import { subscribeDialogState } from './dialogManager.js';

export interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastOptions[]>([]);
  const [hasOpenDialog, setHasOpenDialog] = useState(false);

  useEffect(() => {
    return subscribeDialogState(setHasOpenDialog);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ durationMs = 4000, ...options }: ToastOptions): string => {
      const id = options.id || Math.random().toString(36).substring(2, 9);
      const newToast: ToastOptions = { ...options, id, durationMs };

      setToasts((prev) => [newToast, ...prev.filter((t) => t.id !== id)]);

      return id;
    },
    []
  );

  const success = useCallback(
    (message: string, action?: ToastAction) => toast({ tone: 'success', message, action }),
    [toast]
  );

  const error = useCallback((message: string) => toast({ tone: 'error', message }), [toast]);

  const info = useCallback(
    (message: string, action?: ToastAction) => toast({ tone: 'info', message, action }),
    [toast]
  );

  const warning = useCallback((message: string) => toast({ tone: 'warning', message }), [toast]);

  const undo = useCallback(
    (message: string, onUndo: () => void, durationMs = 4000) =>
      toast({
        tone: 'info',
        message,
        action: { label: 'بازگرداندن', onClick: onUndo, 'data-testid': 'toast-undo-action' },
        durationMs
      }),
    [toast]
  );

  const value: ToastContextValue = {
    toast,
    success,
    error,
    info,
    warning,
    undo,
    dismiss
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast viewport container: floats above mobile TabBar normally, shifts to top when bottom sheets/dialogs open (FRE-191, FRE-198) */}
      <div
        data-testid="toast-container"
        className={`fixed inset-x-0 mx-auto w-fit max-w-[90vw] z-[70] flex flex-col gap-2 items-center pointer-events-none px-4 transition-all duration-200 ${
          hasOpenDialog
            ? 'top-5 md:top-5'
            : 'bottom-22 bottom-[5.5rem] md:bottom-auto md:top-5'
        }`}
      >
        {toasts.map((t) => (
          <Toast
            key={t.id!}
            id={t.id!}
            tone={t.tone}
            message={t.message}
            action={t.action}
            durationMs={t.durationMs}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.displayName = 'ToastProvider';
